import { FileOutlined } from "@ant-design/icons";
import {
  capitalizeWords,
  formatCurrency,
  renderDateFormated,
} from "../../utils";
import { format, isBefore } from "date-fns";
import { saleStatusDict } from "../../utils/helpers/ticketHelpers";
import { SaleProductViewModel } from "../../interfaces";
import { useEffect, useMemo, useRef } from "react";
import { getAssemblyTimelineEventBadge } from "../../utils/helpers/assemblyTimelineEventHelpers";

const getBadgeContent = (value: number) => {
  let amount = 0;
  let color = "";
  if (value > 0 && value < 20000) {
    amount = 1;
    color = "blue";
  } else if (value >= 20000 && value < 50000) {
    amount = 2;
    color = "green";
  } else if (value >= 50000 && value < 80000) {
    amount = 3;
    color = "yellow";
  } else if (value >= 80000 && value < 130000) {
    amount = 4;
    color = "orange";
  } else if (value >= 130000 && value < 200000) {
    amount = 6;
    color = "red";
  } else if (value >= 200000) {
    amount = 8;
    color = "black";
  }

  return { amount, color };
};

const Badge = ({ value }: { value: number }) => {
  const { amount, color } = getBadgeContent(value);
  return (
    <div
      style={{ backgroundColor: color }}
      className={`text-${color === "yellow" ? "black" : "white"} flex justify-center rounded-full shadow w-1/3`}
    >
      {amount} h
    </div>
  );
};

const PaymentStatusBadge = ({ label }: { label: string }) => {
  const statusClassName =
    label === "Efetivada"
      ? "bg-green-500"
      : label === "Promissória"
        ? "bg-purple-800"
        : "bg-pink-500";

  return (
    <span
      className={`inline-flex w-fit items-center rounded-full px-3 py-0.5 text-xs font-semibold text-white shadow ${statusClassName}`}
    >
      {label}
    </span>
  );
};

const AssemblyTypeBadge = ({ ticket }: { ticket: any }) => {
  const { label, backgroundColor, color } = getAssemblyTimelineEventBadge(ticket);

  return (
    <span
      className="inline-flex w-fit items-center rounded-full px-3 py-0.5 text-xs font-semibold shadow"
      style={{ backgroundColor, color }}
    >
      {label}
    </span>
  );
};

function CardDescription({
  type,
  scheduleDate,
  client,
  seller,
  neighbor,
  city,
  state,
  ownerFullName,
  hasAttachments,
  onClick,
  partsRequests,
  paymentAdditions,
  leaderFullName,
  assembleType,
  scheduledBy,
  events,
  saleStatus,
  isFullPromissory,
  saleProducts,
  owner,
  status,
  createdAt,
  priority,
  childFieldReports,
  lastContactAt,
  buildingInProgress,
  minhaVisitaClientName,
  isUnavailableForAssemble,
  assembleUnavailableReason,
  assemblyDate,
  assemblyTicket,
}: any) {
  const showTime = type === "checklist" ? true : false;
  const couponTotalValue = useMemo(
    () =>
      saleProducts?.reduce(
        (acc: number, current: SaleProductViewModel) =>
          (acc =
            acc +
            (current.name.match(/cupom/gi) ? (current.totalValue ?? 0) : 0)),
        0
      ),
    [saleProducts]
  );

  const ref = useRef<HTMLDivElement | null>(null);
  const scheduledDate = useMemo(
    () => [...(events ?? [])]?.reverse?.()?.[0]?.start,
    [events]
  );
  const saleStatusLabel = isFullPromissory
    ? "100% Promissória"
    : saleStatusDict(saleStatus);
  const isAssemblyInProgress = type === "assemble" && assemblyTicket?.inProgress;

  useEffect(() => {
    const cardElement = ref.current?.parentElement?.parentElement;

    if (!cardElement) {
      return;
    }

    if (isAssemblyInProgress) {
      cardElement.style.border = "3px solid #059669";
      cardElement.style.boxShadow = "0 0 0 1px rgba(5, 150, 105, 0.18)";
      return;
    }

    cardElement.style.boxShadow = "";
    cardElement.style.border = "";

    if (
      scheduledDate &&
      type === "checklist" &&
      status === 3 &&
      scheduledDate < new Date().toISOString()
    ) {
      cardElement.style.border = "2px solid red";
    }

    if (priority === 1) {
      cardElement.style.border = "3px solid orange";
    }
  }, [isAssemblyInProgress, scheduledDate, ref, status, type, priority]);
  return (
    <>
      <div ref={ref}>
        {!!scheduledDate && (
          <p
            style={{
              color:
                status === 3 && scheduledDate < new Date().toISOString()
                  ? "red"
                  : "black",
            }}
          >
            <b>Data Checklist:</b>{" "}
            {scheduleDate
              ? renderDateFormated(scheduledDate, false, showTime)
              : "N/A"}
          </p>
        )}
      </div>
      {isAssemblyInProgress && (
        <span className="mb-2 inline-flex w-fit items-center rounded-full bg-emerald-600 px-3 py-0.5 text-xs font-semibold text-white shadow">
          Em montagem
        </span>
      )}
      {type === "checklist" ? (
        <>
          <div>
            <b>Cliente:</b> {capitalizeWords(client)}
          </div>
          <div>
            <b>Vendedor:</b> {capitalizeWords(seller)}
          </div>
          {!!saleStatusLabel && (
            <div className="flex items-center gap-2">
              <b>Sit. Pagamento:</b>
              <PaymentStatusBadge label={saleStatusLabel} />
            </div>
          )}

          {minhaVisitaClientName && (
            <div
              className={
                "flex justify-center bg-green-500 w-fit rounded-full text-white px-5 py-1 text-xs shadow-md my-1"
              }
            >
              Medidas no Minha Visita
            </div>
          )}

          {buildingInProgress && (
            <div
              className={
                "flex justify-center bg-red-600 w-fit rounded-full text-white px-5 py-1 text-xs shadow-md my-1"
              }
            >
              Em Obras
            </div>
          )}

          {!!owner && (
            <div>
              <b>Responsável:</b> {owner}
            </div>
          )}

          {!!lastContactAt && (
            <div>
              <b>Último contato em:</b>{" "}
              {format(new Date(lastContactAt), "dd/MM/yyyy")}
            </div>
          )}

          {!!saleProducts?.length && (
            <>
              <div>
                <b>Valor Cupom:</b> {formatCurrency(couponTotalValue)}
              </div>
              <Badge value={couponTotalValue} />
            </>
          )}
        </>
      ) : null}
      {type === "measuring" && (
        <>
          {minhaVisitaClientName && (
            <div
              className={
                "flex justify-center bg-green-500 w-fit rounded-full text-white px-5 py-1 text-xs shadow-md my-1"
              }
            >
              Medidas no Minha Visita
            </div>
          )}
          {!!events?.length && (
            <div>
              Data Checklist: {format(new Date(events[0].start), "dd/MM/yyyy")}
            </div>
          )}

          {ownerFullName && <div>Medidor: {ownerFullName}</div>}
          {hasAttachments && (
            <div className="w-56 relative">
              <div className=" absolute font-bold italic text-xs bottom-3 right-0">
                <FileOutlined />
              </div>
            </div>
          )}
          <div>
            Bairro: {neighbor ? capitalizeWords(neighbor) : "(Não informado)"}
          </div>
          <div>
            Cidade:{" "}
            {city
              ? `${capitalizeWords(city)}-${state?.toUpperCase()}`
              : "(Não informado)"}
          </div>
        </>
      )}

      {type === "assemble" && (
        <>
          <div className="flex justify-between flex-wrap">
            {!!childFieldReports.length && (
              <div
                className={
                  "flex justify-center bg-blue-500 w-fit rounded-full text-white px-5 py-1 text-xs shadow-md my-1"
                }
              >
                + Assistencia
              </div>
            )}
            {!!partsRequests && (
              <div
                className={
                  "flex justify-center bg-yellow-300 w-fit rounded-full px-5 py-1 text-xs shadow-md my-1"
                }
              >
                Assistencia
              </div>
            )}

            {!!paymentAdditions.length && (
              <div
                className={`flex justify-center ${paymentAdditions.filter((item: any) => item.status === "PENDING_APPROVEMENT").length ? "bg-red-600 text-white" : "bg-green-400"} w-fit rounded-full px-5 py-1 text-xs shadow-md my-1`}
              >
                Adicional $
              </div>
            )}

            {priority === 1 && (
              <div
                className={`flex justify-center bg-orange-600 w-fit rounded-full px-5 py-1 text-xs shadow-md my-1 text-white`}
              >
                Prioridade Alta
              </div>
            )}
            {isUnavailableForAssemble && (
              <div
                className={
                  "flex justify-center bg-red-600 w-fit rounded-full text-white px-5 py-1 text-xs shadow-md my-1"
                }
                title={assembleUnavailableReason}
              >
                Cliente Indisponível
              </div>
            )}
            {hasAttachments && paymentAdditions && partsRequests && <br />}
          </div>
          <div>
            {hasAttachments && (
              <div className="w-full flex justify-end"> 
                <div className="font-bold italic text-xs bottom-3 right-0">
                  <FileOutlined />
                </div>
              </div>
            )}
          </div>
          <div>
            Bairro: {neighbor ? capitalizeWords(neighbor) : "(Não informado)"}
          </div>
          <div>
            Cidade:{" "}
            {city
              ? `${capitalizeWords(city)}-${state?.toUpperCase()}`
              : "(Não informado)"}
          </div>
          {status === 5 &&
            isBefore(new Date("2026-02-09"), new Date(createdAt)) && (
              <div>
                Data Aprovação: {format(new Date(createdAt), "dd/MM/yyyy")}
              </div>
            )}
          {ownerFullName && <div> Montador: {ownerFullName} </div>}
          {assemblyDate && (
            <div>
              <b>Data Montagem:</b> {renderDateFormated(assemblyDate)}
            </div>
          )}
          {assembleType && (
            <div className="flex items-center gap-2">
              <span>Tipo:</span>
              <AssemblyTypeBadge ticket={assemblyTicket} />
            </div>
          )}
          {scheduledBy && (
            <div>
              <b>Agendado por: {scheduledBy}</b>
            </div>
          )}
          {!!lastContactAt && (
            <div>
              <b>Último contato em:</b>{" "}
              {format(new Date(lastContactAt), "dd/MM/yyyy")}
            </div>
          )}
        </>
      )}
    </>
  );
}

export default CardDescription;
